import React, { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { PersonalProfileFormData, PasswordChangeFormData } from '@/types'
import { settingsService } from '@/services/settingsService'
import AvatarUpload from './AvatarUpload'
import PasswordChangeModal from './PasswordChangeModal'

const PersonalSettingsTab: React.FC = () => {
  const { user, updateUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [isAvatarUploading, setIsAvatarUploading] = useState(false)
  
  // 表單狀態
  const [profileData, setProfileData] = useState<PersonalProfileFormData>({
    name: '',
    email: '',
    bio: ''
  })

  // 初始化表單數據
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name,
        email: user.email,
        bio: user.bio || ''
      })
    }
  }, [user])

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    setIsLoading(true)
    try {
      // 更新個人資料 (包含 bio)
      const updatedUser = await settingsService.updateProfile({
        name: profileData.name,
        email: profileData.email,
        bio: profileData.bio
      })
      
      // 更新本地用戶狀態
      updateUser(updatedUser)
      
      alert('設定已儲存')
    } catch (error) {
      console.error('儲存設定失敗:', error)
      alert(error instanceof Error ? error.message : '儲存失敗，請稍後再試')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAvatarChange = async (file: File) => {
    setIsAvatarUploading(true)
    try {
      const updatedUser = await settingsService.uploadAvatar(file)
      
      // 更新本地用戶狀態
      updateUser(updatedUser)
      
      alert('頭像已更新')
    } catch (error) {
      console.error('頭像上傳失敗:', error)
      alert(error instanceof Error ? error.message : '頭像上傳失敗，請稍後再試')
    } finally {
      setIsAvatarUploading(false)
    }
  }

  const handlePasswordChange = async (data: PasswordChangeFormData) => {
    try {
      await settingsService.changePassword(data)
      alert('密碼已修改')
    } catch (error) {
      console.error('密碼修改失敗:', error)
      throw error
    }
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-secondary-600 dark:text-secondary-400">載入中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <form onSubmit={handleProfileSubmit} className="space-y-8">
        {/* 基本資料區塊 */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-primary-800 mb-6">
            基本資料
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 頭像上傳 */}
            <div className="lg:col-span-1">
              <AvatarUpload
                user={user}
                onAvatarChange={handleAvatarChange}
                isLoading={isAvatarUploading}
              />
            </div>
            
            {/* 基本資料表單 */}
            <div className="lg:col-span-2 space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  姓名 *
                </label>
                <input
                  type="text"
                  value={profileData.name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                  className="input-primary"
                  placeholder="請輸入您的姓名"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  電子郵件 *
                </label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                  className="input-primary"
                  placeholder="請輸入您的電子郵件"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  個人簡介
                </label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                  rows={3}
                  className="input-primary resize-none"
                  placeholder="簡單介紹一下自己..."
                />
              </div>
              
              <div className="pt-4">
                <button
                  type="button"
                  onClick={() => setIsPasswordModalOpen(true)}
                  className="btn-secondary"
                >
                  修改密碼
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 操作按鈕 */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => {
              // 重置表單
              if (user) {
                setProfileData({
                  name: user.name,
                  email: user.email,
                  bio: user.bio || ''
                })
              }
            }}
            disabled={isLoading}
            className="btn-secondary"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary"
          >
            {isLoading ? (
              <>
                <div className="loading-spinner mr-2"></div>
                儲存中...
              </>
            ) : (
              '儲存變更'
            )}
          </button>
        </div>
      </form>

      {/* 密碼修改 Modal */}
      <PasswordChangeModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSubmit={handlePasswordChange}
        isLoading={false}
      />
    </div>
  )
}

export default PersonalSettingsTab
